"use client"

import { useEffect, useState, useRef, useCallback } from "react"
import { createPortal } from "react-dom"
import dynamic from "next/dynamic"
import { usePathname } from "next/navigation"
import { trackMetaEvent, setMetaAdvancedMatching } from "@/utils/meta-pixel"
import { trackTikTokEvent, setTikTokAdvancedMatching } from "@/utils/tiktok-pixel"

const GamifiedGrowthPopup = dynamic(() => import("@/components/landing/GamifiedGrowthPopup"), { ssr: false })

const SESSION_KEY = "gamified_popup_shown"
const EXCLUDED_ROOT_TOOL_PATHS = new Set([
    "/instagram-story-viewer",
    "/instagram-web-viewer",
    "/instagram-downloader",
    "/instagram-highlight-viewer",
    "/instagram-reels-downloader",
    "/instagram-profile-analyzer",
])
const LOCALE_PREFIX = /^\/(?:en|es|fr|de)(?=\/|$)/

function isSearchEngine(userAgent: string): boolean {
    const bots = ["Googlebot", "Bingbot", "Slurp", "DuckDuckBot", "Baiduspider", "YandexBot", "Sogou", "Exabot", "facebookexternalhit", "ia_archiver"]
    return bots.some((bot) => userAgent.toLowerCase().includes(bot.toLowerCase()))
}

function isMobileDevice(): boolean {
    if (typeof navigator === "undefined") return false
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

export default function GamifiedPopupLoader() {
    const pathname = usePathname() ?? ""
    const normalizedPathname = pathname.replace(LOCALE_PREFIX, "") || "/"
    const isExcludedPath =
        pathname.includes("/blog") ||
        pathname.includes("/free-instagram-tools") ||
        EXCLUDED_ROOT_TOOL_PATHS.has(normalizedPathname)
    const [showPopup, setShowPopup] = useState(false)
    const [isMounted, setIsMounted] = useState(false)

    const hasTriggeredRef = useRef(false)
    const timeTimerRef = useRef<NodeJS.Timeout | null>(null)
    const mobileDelayTimerRef = useRef<NodeJS.Timeout | null>(null)
    const cleanupFnsRef = useRef<(() => void)[]>([])
    const lastScrollYRef = useRef(0)

    const clearTriggers = useCallback(() => {
        cleanupFnsRef.current.forEach((fn) => fn())
        cleanupFnsRef.current = []

        if (timeTimerRef.current) {
            clearTimeout(timeTimerRef.current)
            timeTimerRef.current = null
        }

        if (mobileDelayTimerRef.current) {
            clearTimeout(mobileDelayTimerRef.current)
            mobileDelayTimerRef.current = null
        }
    }, [])

    useEffect(() => {
        setIsMounted(true)
    }, [])

    const triggerPopup = useCallback(() => {
        if (hasTriggeredRef.current) return
        hasTriggeredRef.current = true
        setShowPopup(true)
        clearTriggers()
    }, [clearTriggers])

    const handleClose = useCallback(() => {
        setShowPopup(false)
        sessionStorage.setItem(SESSION_KEY, "true")

        // Ensure all timers cleared
        if (timeTimerRef.current) clearTimeout(timeTimerRef.current)
        if (mobileDelayTimerRef.current) clearTimeout(mobileDelayTimerRef.current)
    }, [])

    const handleEmailSubmit = useCallback(async (email: string, discountCode: string) => {
        // Generate event IDs for dedup
        const metaEventId = `lead_popup_${Date.now()}`
        const tiktokEventId = `tt_lead_popup_${Date.now()}`

        // TikTok Advanced Matching
        setTikTokAdvancedMatching(email)

        // Meta Advanced Matching (re-init with email)
        setMetaAdvancedMatching("753687921113071", email)

        // Google user-provided data
        if (typeof window !== "undefined" && (window as any).gtag) {
            (window as any).gtag("set", "user_data", { email: email.toLowerCase().trim() })
        }

        // Meta Lead event
        trackMetaEvent("Lead", {
            content_name: "Exit Intent Popup",
            value: 5.00,
            currency: "USD"
        }, metaEventId)

        // TikTok SubmitForm event
        trackTikTokEvent("SubmitForm", {
            content_name: "Exit Intent Popup",
            value: 5.00,
            currency: "USD"
        }, tiktokEventId)

        // Google Ads Lead conversion
        if (typeof window !== "undefined" && (window as any).gtag) {
            (window as any).gtag("event", "generate_lead", {
                value: 5.00,
                currency: "USD"
            })
        }

        // Set _of_em cookie for returning visitor identify (90 days)
        try {
            const d = new Date()
            d.setTime(d.getTime() + 7776e6)
            document.cookie = `_of_em=${encodeURIComponent(email.toLowerCase().trim())}; expires=${d.toUTCString()}; path=/; SameSite=Lax`
        } catch (e) {}

        // Helper to read attribution cookies
        const readCookie = (name: string) => {
            const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
            return match ? decodeURIComponent(match[1]) : null
        }

        try {
            await fetch("/api/popup-lead", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email,
                    discountCode,
                    pageUrl: window.location.href,
                    metaEventId,
                    tiktokEventId,
                    // Attribution cookies for server-side CAPI
                    fbp: readCookie("_fbp"),
                    fbc: readCookie("_fbc"),
                    fbclid: readCookie("fbclid"),
                    ttclid: readCookie("ttclid"),
                    ttp: readCookie("_ttp"),
                    gclidRaw: readCookie("gclid_raw"),
                    utmSource: readCookie("utm_source"),
                    utmMedium: readCookie("utm_medium"),
                    utmCampaign: readCookie("utm_campaign"),
                })
            })
        } catch (e) {
            console.error("Failed to submit popup lead:", e)
        }
    }, [])

    useEffect(() => {
        if (typeof window === "undefined") return

        // Skip on pages that have their own popup (DelayedModalLoader)
        if (isExcludedPath) {
            clearTriggers()
            setShowPopup(false)
            return
        }

        // Bot detection
        if (isSearchEngine(navigator.userAgent)) return

        // Session frequency control - once per session
        if (sessionStorage.getItem(SESSION_KEY)) return
        if (hasTriggeredRef.current) return

        const isMobile = isMobileDevice()
        lastScrollYRef.current = window.scrollY

        const setupTriggers = () => {
            // 1. Exit intent (desktop) - mouse leaves top of viewport
            if (!isMobile) {
                const handleMouseOut = (e: MouseEvent) => {
                    if (e.clientY < 0) triggerPopup()
                }
                document.addEventListener("mouseout", handleMouseOut)
                cleanupFnsRef.current.push(() => document.removeEventListener("mouseout", handleMouseOut))
            }

            // 2. Scroll-up velocity (mobile) - simulated exit intent
            if (isMobile) {
                const VELOCITY_THRESHOLD = -50
                let lastY = window.scrollY

                const handleScroll = () => {
                    const currentY = window.scrollY
                    const velocity = currentY - lastY
                    lastY = currentY

                    // Trigger if scrolling up fast near top of page
                    if (velocity < VELOCITY_THRESHOLD && currentY < window.innerHeight) {
                        triggerPopup()
                    }
                }
                window.addEventListener("scroll", handleScroll, { passive: true })
                cleanupFnsRef.current.push(() => window.removeEventListener("scroll", handleScroll))
            }

            // 3. Scroll depth - 50% of page
            const handleScrollDepth = () => {
                const scrollHeight = Math.max(
                    document.body.scrollHeight,
                    document.documentElement.scrollHeight,
                    document.body.offsetHeight,
                    document.documentElement.offsetHeight
                )
                const scrolled = (window.scrollY + window.innerHeight) / scrollHeight
                if (scrolled >= 0.5) triggerPopup()
            }
            window.addEventListener("scroll", handleScrollDepth, { passive: true })
            cleanupFnsRef.current.push(() => window.removeEventListener("scroll", handleScrollDepth))

            // 4. Time on page - 15 seconds
            timeTimerRef.current = setTimeout(triggerPopup, 15000)
        }

        // Mobile: 2s delay to let page settle
        if (isMobile) {
            mobileDelayTimerRef.current = setTimeout(setupTriggers, 2000)
        } else {
            setupTriggers()
        }

        return clearTriggers
    }, [clearTriggers, isExcludedPath, triggerPopup])

    if (!isMounted || !showPopup || isExcludedPath) return null

    return createPortal(
        <GamifiedGrowthPopup
            isOpen={true}
            onClose={handleClose}
            onEmailSubmit={handleEmailSubmit}
        />,
        document.body
    )
}
